import { Connection, VersionedTransaction, TransactionSignature, PublicKey, LAMPORTS_PER_SOL, ComputeBudgetProgram } from '@solana/web3.js';

// Configuration - Aggressive strategy for guaranteed fast execution
export const TRANSACTION_CONFIG = {
  CONFIRMATION_TIMEOUT: 5000,
  CONFIRMATION_TIMEOUT_FAST: 2000,
  MAX_RETRIES: 1,
  POLL_INTERVAL: 250,
  POLL_INTERVAL_FAST: 150,
  RETRY_DELAY_BASE: 1000,
  PRIORITY_FEE: 0.0001, // Legacy fallback - actual fees set in PumpService
  PLATFORM_FEE_PERCENT: 1,
  PLATFORM_FEE_WALLET: '42kX3VMXNNF8eadz9RtpQbseKCGYcwK5Z8iEsxxSRtzg',
  // Reasonable fee strategy for fast execution without excessive cost
  AGGRESSIVE_STRATEGY: {
    PRIORITY_FEE_MICROLAMPORTS: 500_000, // 500k microlamports/CU
    JITO_TIP_SOL: 0.01, // 0.01 SOL tip
    COMPUTE_UNITS: 200_000, // 200k CU limit
    SKIP_PREFLIGHT: true, // Skip simulation for speed
    MEV_PROTECTION: true
  }
} as const;

// Error types
export enum TransactionErrorType {
  WALLET_NOT_CONNECTED = 'WALLET_NOT_CONNECTED',
  API_ERROR = 'API_ERROR',
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT = 'TIMEOUT',
  TRANSACTION_FAILED = 'TRANSACTION_FAILED',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  INSUFFICIENT_FUNDS = 'INSUFFICIENT_FUNDS'
}

export const ERROR_MESSAGES = {
  [TransactionErrorType.WALLET_NOT_CONNECTED]: 'Please connect your wallet to continue',
  [TransactionErrorType.API_ERROR]: 'Trading service temporarily unavailable',
  [TransactionErrorType.NETWORK_ERROR]: 'Network connection issue. Please try again',
  [TransactionErrorType.TIMEOUT]: 'Transaction took too long to confirm',
  [TransactionErrorType.TRANSACTION_FAILED]: 'Transaction failed on blockchain',
  [TransactionErrorType.UNKNOWN_ERROR]: 'An unexpected error occurred',
  [TransactionErrorType.INSUFFICIENT_FUNDS]: 'Insufficient funds'
} as const;

// Interfaces
export interface TransactionResult {
  success: boolean;
  signature?: string;
  error?: string;
  errorType?: TransactionErrorType;
  status?: 'pending' | 'confirmed' | 'failed' | 'timeout' | 'confirmed-by-balance';
  solSpent?: number;
  solReceived?: number;
  tokensReceived?: number;
  tokensSold?: number;
  effectivePrice?: number;
  effectiveMarketCap?: number;
  accountRecovery?: {
    solRecovered: number;
    closeSignature?: string;
    closeSuccess: boolean;
  };
  platformFee?: {
    amount: number;
    signature?: string;
    success: boolean;
  };
  preBalances?: {
    sol: number;
    token: number;
  };
  postBalances?: {
    sol: number;
    token: number;
  };
  // Voucher-related fields
  isVoucherTrade?: boolean;
  creditsRemaining?: number;
  // Cleanup flag
  isCleanup?: boolean;
}

export interface TokenDetails {
  mint: string;
  exchanges?: any[]; // Optional exchanges array for voucher trading
}

export interface PumpPortalRequest {
  publicKey: string;
  action: 'buy' | 'sell';
  mint: string;
  denominatedInSol: string;
  amount: number | string;
  slippage: number;
  priorityFee: number;
  pool: 'auto';
}

/**
 * Create standardized error result
 */
export function createErrorResult(
  errorType: TransactionErrorType, 
  details?: string,
  logger?: (type: string, data: any) => void
): TransactionResult {
  const message = ERROR_MESSAGES[errorType];
  const fullError = details ? `${message}: ${details}` : message;
  
  logger?.('Error', { errorType, message: fullError });
  
  return {
    success: false,
    error: fullError,
    errorType,
    status: 'failed'
  };
}

/**
 * Wait for transaction confirmation with optimized polling for 'processed' level
 */
export async function waitForConfirmation(
  connection: Connection,
  signature: TransactionSignature,
  logger?: (type: string, data: any) => void
): Promise<{ confirmed: boolean; errorType?: TransactionErrorType; error?: string }> {
  logger?.('ConfirmationStart', { signature, commitmentLevel: 'processed' });
  
  const startTime = Date.now();
  let pollCount = 0;
  
  // Use fast timeout for 'processed' level
  const timeout = TRANSACTION_CONFIG.CONFIRMATION_TIMEOUT_FAST;
  
  while (Date.now() - startTime < timeout) {
    try {
      const rpcStartTime = Date.now();
      const status = await connection.getSignatureStatus(signature);
      const rpcDuration = Date.now() - rpcStartTime;
      pollCount++;
      
      // Log every status check for debugging
      const currentStatus = status.value?.confirmationStatus;
      logger?.('ConfirmationPoll', { 
        signature: signature.slice(0, 8), 
        pollCount, 
        currentStatus, 
        hasError: !!status.value?.err,
        elapsed: Date.now() - startTime,
        rpcDuration // Add RPC call timing
      });
      
      // Check if we've reached 'processed' level or higher
      const isConfirmed = currentStatus === 'processed' || currentStatus === 'confirmed' || currentStatus === 'finalized';
      
      if (isConfirmed) {
        if (status.value?.err) {
          const error = `Transaction failed: ${JSON.stringify(status.value.err)}`;
          logger?.('ConfirmationFailed', { signature, error, pollCount, commitmentLevel: 'processed' });
          return { 
            confirmed: false, 
            errorType: TransactionErrorType.TRANSACTION_FAILED,
            error 
          };
        }
        
        logger?.('ConfirmationSuccess', { 
          signature, 
          status: currentStatus,
          targetLevel: 'processed',
          pollCount,
          duration: Date.now() - startTime
        });
        return { confirmed: true };
      }
      
      // Optimized polling strategy: start fast, then slow down
      let delay: number;
      if (pollCount <= 5) {
        // First 5 polls: fast (200ms) - most transactions confirm quickly
        delay = TRANSACTION_CONFIG.POLL_INTERVAL_FAST;
      } else if (pollCount <= 10) {
        // Next 5 polls: medium speed (250ms)
        delay = TRANSACTION_CONFIG.POLL_INTERVAL;
      } else {
        // Later polls: slower with exponential backoff (up to 2s max)
        const exponentialDelay = Math.min(
          TRANSACTION_CONFIG.POLL_INTERVAL * Math.pow(1.5, Math.floor((pollCount - 10) / 3)), 
          2000
        );
        delay = exponentialDelay;
      }
      
      // Add small jitter to avoid thundering herd
      const jitter = Math.random() * 50;
      await new Promise(resolve => setTimeout(resolve, delay + jitter));
      
    } catch (error: any) {
      logger?.('ConfirmationError', { signature, error: error.message, pollCount, commitmentLevel: 'processed' });
      await new Promise(resolve => setTimeout(resolve, TRANSACTION_CONFIG.RETRY_DELAY_BASE));
    }
  }
  
  logger?.('ConfirmationTimeout', { 
    signature, 
    pollCount, 
    duration: Date.now() - startTime,
    commitmentLevel: 'processed',
    timeout
  });
  return { 
    confirmed: false, 
    errorType: TransactionErrorType.TIMEOUT,
    error: 'Transaction confirmation timeout (processed)'
  };
}

/**
 * Get SOL balance for wallet
 */
export async function getSolBalance(
  connection: Connection,
  walletAddress: string,
  logger?: (type: string, data: any) => void
): Promise<number> {
  try {
    const balance = await connection.getBalance(new PublicKey(walletAddress));
    return balance / LAMPORTS_PER_SOL;
  } catch (error: any) {
    logger?.('SolBalanceError', { error: error.message });
    return 0;
  }
}

/**
 * Get token balance for specific mint
 */
export async function getTokenBalance(
  connection: Connection,
  walletAddress: string,
  tokenMint: string,
  logger?: (type: string, data: any) => void
): Promise<number> {
  try {
    const tokenAccounts = await connection.getParsedTokenAccountsByOwner(
      new PublicKey(walletAddress),
      { mint: new PublicKey(tokenMint) }
    );
    
    if (tokenAccounts.value.length === 0) {
      return 0;
    }
    
    // Sum all token account balances for this mint
    const totalBalance = tokenAccounts.value.reduce((sum, account) => {
      const balance = account.account.data.parsed.info.tokenAmount.uiAmount || 0;
      return sum + balance;
    }, 0);
    
    return totalBalance;
  } catch (error: any) {
    logger?.('TokenBalanceError', { mint: tokenMint, error: error.message });
    return 0;
  }
}

/**
 * Calculate transaction economics from before/after balances
 */
export function calculateTransactionEconomics(
  type: 'buy' | 'sell',
  preBalances: { sol: number; token: number },
  postBalances: { sol: number; token: number },
  intendedSolAmount?: number
): {
  solSpent: number;
  tokensReceived: number;
  solReceived: number;
  tokensSold: number;
  effectivePrice: number;
  accountClosureRecovery?: number;
} {
  const tokenChange = postBalances.token - preBalances.token;
  const solChange = postBalances.sol - preBalances.sol;

  if (type === 'buy') {
    // For buy: SOL balance should decrease (negative solChange), token balance should increase (positive tokenChange)
    const solSpent = Math.abs(solChange); // Convert negative to positive
    const tokensReceived = tokenChange > 0 ? tokenChange : 0; // Should be positive
    const effectivePrice = tokensReceived > 0 && solSpent > 0 ? solSpent / tokensReceived : 0;
    
    return {
      solSpent,
      tokensReceived,
      solReceived: 0,
      tokensSold: 0,
      effectivePrice
    };
  } else {
    // For sell: SOL balance should increase, token balance should decrease
    // BUT we need to account for account closure recovery
    const tokensSold = Math.abs(tokenChange); // Convert negative to positive
    const grossSolReceived = solChange > 0 ? solChange : 0; // Total SOL increase
    
    // Estimate account closure recovery (typical rent-exempt amount)
    // This is approximate - the exact amount depends on the account size
    const ESTIMATED_ACCOUNT_CLOSURE_RECOVERY = 0.00203928;
    
    // If we sold ALL tokens (token balance is now 0), account was likely closed
    const accountWasClosed = postBalances.token === 0;
    const accountClosureRecovery = accountWasClosed ? ESTIMATED_ACCOUNT_CLOSURE_RECOVERY : 0;
    
    // Net SOL received from the actual trade (excluding account closure recovery)
    const netSolReceived = Math.max(0, grossSolReceived - accountClosureRecovery);
    
    const effectivePrice = tokensSold > 0 && netSolReceived > 0 ? netSolReceived / tokensSold : 0;
    
    return {
      solSpent: 0,
      tokensReceived: 0,
      solReceived: netSolReceived,
      tokensSold,
      effectivePrice,
      accountClosureRecovery: accountWasClosed ? accountClosureRecovery : undefined
    };
  }
}

/**
 * Call PumpPortal API to get serialized transaction
 */
export async function callPumpPortalAPI(
  walletAddress: string,
  action: 'buy' | 'sell',
  tokenDetails: TokenDetails,
  amount: number | string,
  denominatedInSol: boolean,
  slippagePercent: number,
  logger?: (type: string, data: any) => void
): Promise<{ success: boolean; transaction?: VersionedTransaction; errorType?: TransactionErrorType; error?: string }> {
  try {
    logger?.('APICall', { action, mint: tokenDetails.mint, amount, denominatedInSol });
    
    const requestBody: PumpPortalRequest = {
      publicKey: walletAddress,
      action,
      mint: tokenDetails.mint,
      denominatedInSol: denominatedInSol.toString(),
      amount,
      slippage: slippagePercent,
      priorityFee: TRANSACTION_CONFIG.PRIORITY_FEE,
      pool: 'auto'
    };
    
    const response = await fetch('https://pumpportal.fun/api/trade-local', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });
    
    if (response.status !== 200) {
      const errorText = await response.text();
      logger?.('APIError', { status: response.status, error: errorText });
      return { 
        success: false, 
        errorType: TransactionErrorType.API_ERROR,
        error: `API error (${response.status}): ${response.statusText}`
      };
    }
    
    const data = await response.arrayBuffer();
    const tx = VersionedTransaction.deserialize(new Uint8Array(data));
    
    logger?.('APISuccess', { action, mint: tokenDetails.mint });
    return { success: true, transaction: tx };
    
  } catch (error: any) {
    logger?.('APIException', { error: error.message });
    return { 
      success: false, 
      errorType: TransactionErrorType.NETWORK_ERROR,
      error: error.message || 'API call failed'
    };
  }
}
/**
 * Get dynamic priority fee estimate for a transaction
 */
export async function getPriorityFeeEstimate(
  connection: Connection,
  transaction: VersionedTransaction,
  priorityLevel: 'Low' | 'Medium' | 'High' | 'VeryHigh' = 'High',
  logger?: (type: string, data: any) => void
): Promise<number> {
  try {
    logger?.('PriorityFeeStart', { priorityLevel });
    
    // Serialize the transaction for the API
    const serializedTransaction = Buffer.from(transaction.serialize()).toString('base64');
    
    // Call Helius Priority Fee API
    const response = await fetch(connection.rpcEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: '1',
        method: 'getPriorityFeeEstimate',
        params: [
          {
            transaction: serializedTransaction,
            options: { 
              priorityLevel: priorityLevel,
              recommended: true 
            }
          }
        ]
      })
    });
    
    const result = await response.json();
    
    if (result.error) {
      logger?.('PriorityFeeError', { error: result.error });
      // Fallback to static fee if API fails
      return TRANSACTION_CONFIG.PRIORITY_FEE * 1_000_000; // Convert SOL to microlamports
    }
    
    const priorityFee = result.result?.priorityFeeEstimate || 0;
    logger?.('PriorityFeeSuccess', { priorityLevel, priorityFee });
    
    return priorityFee;
  } catch (error: any) {
    logger?.('PriorityFeeException', { error: error.message });
    // Fallback to static fee if anything fails
    return TRANSACTION_CONFIG.PRIORITY_FEE * 1_000_000; // Convert SOL to microlamports
  }
}

/**
 * Add priority fee instruction to transaction
 */
export function addPriorityFeeToTransaction(
  transaction: VersionedTransaction,
  priorityFeeMicroLamports: number,
  logger?: (type: string, data: any) => void
): VersionedTransaction {
  try {
    logger?.('AddingPriorityFee', { priorityFeeMicroLamports });
    
    // Create priority fee instruction
    const priorityFeeInstruction = ComputeBudgetProgram.setComputeUnitPrice({
      microLamports: priorityFeeMicroLamports
    });
    
    // We need to rebuild the transaction with the priority fee instruction first
    // This is complex with VersionedTransaction, so we'll handle this in PumpService
    logger?.('PriorityFeeInstructionCreated', { microLamports: priorityFeeMicroLamports });
    
    return transaction;
  } catch (error: any) {
    logger?.('PriorityFeeAddError', { error: error.message });
    return transaction;
  }
}

/**
 * Jito Block Engine configuration for direct submission
 */
const JITO_CONFIG = {
  ENDPOINTS: [
    'https://ny.mainnet.block-engine.jito.wtf/api/v1/transactions',      // US East (closest for most users)
    'https://slc.mainnet.block-engine.jito.wtf/api/v1/transactions',     // US West
    'https://london.mainnet.block-engine.jito.wtf/api/v1/transactions',  // Europe
    'https://amsterdam.mainnet.block-engine.jito.wtf/api/v1/transactions', // Europe backup
    'https://singapore.mainnet.block-engine.jito.wtf/api/v1/transactions' // Asia
  ],
  TIP_ACCOUNTS: [
    "4ACfpUFoaSD9bfPdeu6DBt89gB6ENTeHBXCAi87NhDEE",
    "D2L6yPZ2FmmmTKPgzaMKdhu6EWZcTpLy1Vhx8uvZe7NZ",
    "9bnz4RShgq1hAnLnZbP8kbgBg1kEmcJBYQq3gQbmnSta",
    "5VY91ws6B2hMmBFRsXkoAAdsPHBJwRfBht4DXox3xkwn",
    "2nyhqdwKcJZR2vcqCyrYsaPVdAnFoJjiksCXJ7hfEYgD"
  ],
  TIP_AMOUNT_SOL: 0.01, // Match Pump's aggressive tip strategy
  TIMEOUT_MS: 5000
};

/**
 * Send transaction via Jito Block Engine for MEV protection and fast execution
 */
export async function sendViaJito(
  signedTransaction: VersionedTransaction,
  logger?: (type: string, data: any) => void
): Promise<{ success: boolean; signature?: string; error?: string }> {
  try {
    logger?.('JitoSendStart', { 
      endpointsCount: JITO_CONFIG.ENDPOINTS.length,
      tipAmount: JITO_CONFIG.TIP_AMOUNT_SOL 
    });
    
    const serializedTransaction = Buffer.from(signedTransaction.serialize()).toString('base64');
    
    // Try endpoints in order (closest first)
    for (let i = 0; i < JITO_CONFIG.ENDPOINTS.length; i++) {
      const endpoint = JITO_CONFIG.ENDPOINTS[i];
      const region = endpoint.includes('ny') ? 'US-East' : 
                    endpoint.includes('slc') ? 'US-West' :
                    endpoint.includes('london') ? 'EU-London' :
                    endpoint.includes('amsterdam') ? 'EU-Amsterdam' : 'Asia';
      
      try {
        logger?.('JitoEndpointTry', { endpoint: region, attempt: i + 1 });
        
        const requestStart = Date.now();
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            // Note: Add x-jito-auth header here if you get API key
          },
          body: JSON.stringify({
            jsonrpc: '2.0',
            id: Date.now().toString(),
            method: 'sendTransaction',
            params: [
              serializedTransaction,
              {
                encoding: 'base64',
                skipPreflight: true, // Skip simulation for speed
                maxRetries: 0
              }
            ]
          }),
          signal: AbortSignal.timeout(JITO_CONFIG.TIMEOUT_MS)
        });
        
        const requestDuration = Date.now() - requestStart;
        
        if (!response.ok) {
          logger?.('JitoEndpointHttpError', { 
            endpoint: region, 
            status: response.status, 
            statusText: response.statusText,
            duration: requestDuration
          });
          continue;
        }
        
        const result = await response.json();
        
        if (result.error) {
          logger?.('JitoEndpointRpcError', { 
            endpoint: region, 
            error: result.error,
            duration: requestDuration
          });
          continue; // Try next endpoint
        }
        
        const signature = result.result;
        const bundleId = response.headers.get('x-bundle-id');
        
        logger?.('JitoSendSuccess', { 
          endpoint: region, 
          signature: signature.slice(0, 8) + '...',
          bundleId: bundleId?.slice(0, 8) + '...' || 'N/A',
          duration: requestDuration,
          attempt: i + 1
        });
        
        return { success: true, signature };
        
      } catch (endpointError: any) {
        const errorType = endpointError.name === 'TimeoutError' ? 'timeout' : 'network';
        logger?.('JitoEndpointError', { 
          endpoint: region, 
          error: endpointError.message,
          errorType,
          attempt: i + 1
        });
        continue; // Try next endpoint
      }
    }
    
    logger?.('JitoSendFailed', { error: 'All Jito endpoints failed' });
    return { success: false, error: 'All Jito Block Engine endpoints failed' };
    
  } catch (error: any) {
    logger?.('JitoSendException', { error: error.message });
    return { success: false, error: error.message };
  }
} 